import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Text,
  Heading,
  VStack,
  useBreakpointValue,
  Tooltip,
  HStack,
  Spinner,
} from '@chakra-ui/react'
import { getAssetList } from '../../services/apiService'

interface Region {
  name: string
  x: number // 지도 내 상대적 위치 (%)
  y: number
  width: number
  height: number
}

interface Asset {
  id: number
  location: string
  area: number
  appraisalValue: string
  note: string | null
  createdAt: string
}

interface RegionSummary {
  totalArea: number
  totalAppraisalValue: number
  count: number
}

const RegionMap: React.FC = () => {
  const { t } = useTranslation()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null)
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(true)

  // 자산 데이터 로드
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const { data: res } = await getAssetList()
        if (res.statusCode === 200) {
          setAssets(res.data.assets)
        }
      } catch (error) {
        console.error('Error fetching assets:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchAssets()
  }, [])

  // 지역별 자산 합계 계산
  const getRegionSummary = (regionName: string): RegionSummary => {
    const regionAssets = assets.filter(asset => asset.location === regionName)
    return {
      totalArea: regionAssets.reduce((sum, asset) => sum + asset.area, 0),
      totalAppraisalValue: regionAssets.reduce((sum, asset) => sum + Number(asset.appraisalValue), 0),
      count: regionAssets.length
    }
  }

  // 숫자 포맷팅 (천단위 콤마)
  const formatNumber = (num: number) => {
    return num.toLocaleString()
  }

  const regions: Region[] = [
    { name: '경기도', x: 40, y: 5, width: 15, height: 20 },
    { name: '강원도', x: 55, y: 5, width: 15, height: 20 },
    { name: '서울특별시', x: 25, y: 5, width: 15, height: 10 },
    { name: '인천광역시', x: 25, y: 15, width: 15, height: 10 },
    { name: '충청남도', x: 15, y: 25, width: 15, height: 20 },
    { name: '대전광역시', x: 30, y: 25, width: 15, height: 10 },
    { name: '세종특별자치시', x: 30, y: 35, width: 15, height: 10 },
    { name: '충청북도', x: 45, y: 25, width: 15, height: 20 },
    { name: '경상북도', x: 60, y: 25, width: 15, height: 20 },
    { name: '전라북도', x: 30, y: 45, width: 15, height: 15 },
    { name: '광주광역시', x: 30, y: 60, width: 15, height: 10 },
    { name: '전라남도', x: 30, y: 70, width: 15, height: 15 },
    { name: '대구광역시', x: 45, y: 45, width: 30, height: 10 },
    { name: '경상남도', x: 45, y: 55, width: 15, height: 20 },
    { name: '제주특별자치도', x: 20, y: 88, width: 15, height: 10 },
    { name: '울산광역시', x: 60, y: 55, width: 15, height: 10 },
    { name: '부산광역시', x: 60, y: 65, width: 15, height: 10 },
  ]

  // 포인트 컬러
  const pointColor = '#A74230'

  // 현재 호버된 지역의 요약 정보
  const hoveredSummary = hoveredRegion ? getRegionSummary(hoveredRegion) : null

  return (
    <Box bg="gray.900" py={12} px={4} w="100%">
      <VStack spacing={8}>
        <Box textAlign="center" mb={2}>
          <Heading
            as="h2"
            size={isMobile ? 'lg' : 'xl'}
            mb={2}
            color="white"
          >
            지역별 서비스
          </Heading>
          <Text fontSize="md" color="gray.400">
            마우스를 올려보세요
          </Text>
        </Box>

        <Box
          position="relative"
          w="100%"
          maxW={isMobile ? '100%' : '900px'}
          h={isMobile ? '600px' : '700px'}
          mx="auto"
          bgGradient="linear(to-b, #87CEEB, #B0E0E6, #ADD8E6)"
          borderRadius="xl"
          border="2px solid"
          borderColor="gray.700"
          overflow="hidden"
          boxShadow="lg"
        >
          {/* 지도 배경 이미지 */}
          <Box
            position="absolute"
            inset={0}
            bgImage="/images/map.png"
            bgSize="60%"
            bgPosition="center"
            bgRepeat="no-repeat"
            opacity={0.8}
          />

          {/* 지역 영역들 */}
          {regions.map((region, index) => {
            const isHovered = hoveredRegion === region.name
            const summary = getRegionSummary(region.name)
            const hasAssets = summary.count > 0
            
            return (
              <Tooltip
                key={index}
                label={region.name}
                placement="top"
                hasArrow
                bg="brand.500"
                color="white"
              >
                <Box
                  position="absolute"
                  left={`${region.x}%`}
                  top={`${region.y}%`}
                  w={`${region.width}%`}
                  h={`${region.height}%`}
                  bg="transparent"
                  borderRadius="lg"
                  border="2px solid"
                  borderColor={isHovered ? pointColor : hasAssets ? 'orange.400' : 'transparent'}
                  cursor="pointer"
                  transform={isHovered ? 'scale(1.2) translateZ(0)' : 'scale(1) translateZ(0)'}
                  zIndex={isHovered ? 10 : 1}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  boxShadow={isHovered ? '0 15px 30px rgba(0, 0, 0, 0.3)' : 'none'}
                  onMouseEnter={() => setHoveredRegion(region.name)}
                  onMouseLeave={() => setHoveredRegion(null)}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    fontSize={isMobile ? 'xs' : 'sm'}
                    fontWeight="bold"
                    color={isHovered ? 'white' : 'gray.700'}
                    textAlign="center"
                    px={2}
                    noOfLines={1}
                    bg={isHovered ? 'rgba(255, 255, 255, 0.2)' : hasAssets ? 'orange.100' : 'whiteAlpha.700'}
                    borderRadius="md"
                    py={1}
                  >
                    {region.name}
                  </Text>
                </Box>
              </Tooltip>
            )
          })}

          {/* 호버된 지역 정보 표시 */}
          {hoveredRegion && (
            <Box
              position="absolute"
              bottom={4}
              left="50%"
              transform="translateX(-50%)"
              bg="white"
              px={6}
              py={4}
              borderRadius="xl"
              boxShadow="xl"
              border="2px solid"
              borderColor={pointColor}
              zIndex={20}
              animation="fadeIn 0.3s ease-in"
              minW="280px"
              pointerEvents="none"
            >
              {loading ? (
                <HStack justify="center">
                  <Spinner size="sm" color={pointColor} />
                </HStack>
              ) : (
                <VStack spacing={2} align="stretch">
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={pointColor}
                    textAlign="center"
                  >
                    {hoveredRegion}
                  </Text>
                  {hoveredSummary && hoveredSummary.count > 0 ? (
                    <>
                      <Box h="1px" bg="gray.200" />
                      <HStack justify="space-between" fontSize="sm">
                        <Text color="gray.600">총 면적:</Text>
                        <Text fontWeight="bold" color="gray.800">
                          {formatNumber(hoveredSummary.totalArea)} m²
                        </Text>
                      </HStack>
                      <HStack justify="space-between" fontSize="sm">
                        <Text color="gray.600">총 감정가:</Text>
                        <Text fontWeight="bold" color="gray.800">
                          {formatNumber(hoveredSummary.totalAppraisalValue)} 원
                        </Text>
                      </HStack>
                      <HStack justify="space-between" fontSize="xs" mt={1}>
                        <Text color="gray.500">등록 자산:</Text>
                        <Text color="gray.500">{hoveredSummary.count}건</Text>
                      </HStack>
                    </>
                  ) : (
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      등록된 자산이 없습니다
                    </Text>
                  )}
                </VStack>
              )}
            </Box>
          )}
        </Box>

        {/* 지역 목록 */}
        <Box
          w="100%"
          maxW={isMobile ? '100%' : '800px'}
          mx="auto"
          p={6}
          bg="gray.900"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.700"
          boxShadow="md"
          mt={4}
        >
          <Text
            fontSize="sm"
            fontWeight="bold"
            color="gray.300"
            mb={4}
            textAlign="center"
          >
            서비스 지역
          </Text>
          <Box
            display="grid"
            gridTemplateColumns={isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)'}
            gap={3}
          >
            {regions.map((region, index) => {
              const isHovered = hoveredRegion === region.name
              const summary = getRegionSummary(region.name)
              const hasAssets = summary.count > 0
              return (
                <Box
                  key={index}
                  px={3}
                  py={2}
                  borderRadius="md"
                  bg={isHovered ? 'rgba(167, 66, 48, 0.3)' : hasAssets ? 'rgba(237, 137, 54, 0.2)' : 'gray.800'}
                  border="1px solid"
                  borderColor={isHovered ? pointColor : hasAssets ? 'orange.400' : 'gray.700'}
                  cursor="pointer"
                  transition="all 0.2s"
                  onMouseEnter={() => setHoveredRegion(region.name)}
                  onMouseLeave={() => setHoveredRegion(null)}
                  textAlign="center"
                >
                  <Text
                    fontSize="xs"
                    fontWeight={isHovered || hasAssets ? 'bold' : 'normal'}
                    color={isHovered ? 'white' : hasAssets ? 'orange.200' : 'gray.300'}
                  >
                    {region.name}
                  </Text>
                  {hasAssets && (
                    <Text fontSize="10px" color="gray.500" mt={1}>
                      {summary.count}건
                    </Text>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>
      </VStack>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
    </Box>
  )
}

export default RegionMap
